import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';

import { Observable, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

import { environment } from '@env/environment';
import { AuthStore, RefreshOutcome } from '@store/auth.store';

type AuthStoreInstance = InstanceType<typeof AuthStore>;

const AUTH_PATH = '/auth';
const GEO_API_URL = environment.geoApiUrl;
const UNAUTHORIZED = 401;

/**
 * Adds the bearer token to every call except `/auth` and the geo API. A token already past (or
 * within 30 s of) its expiry is renewed through the store's shared refresh before the call goes
 * out, so in-page calls made long after the last guard run do not hit a 401 first; when that
 * renewal fails the call is not sent and the session ends. On a 401 the request is retried once
 * with the refreshed token; a refresh that fails ends the session keeping the current URL to come
 * back to. A stale refresh (its session already ended or replaced) ends
 * nothing: the original 401 is rethrown. A replayed request that fails keeps its own error.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authStore = inject(AuthStore);
  const router = inject(Router);

  if (req.url.includes(AUTH_PATH) || (!!GEO_API_URL && req.url.startsWith(GEO_API_URL))) {
    return next(req);
  }

  if (
    authStore.isAuthenticated() &&
    authStore.hasRefreshToken() &&
    authStore.isAccessTokenExpired()
  ) {
    return authStore
      .refresh()
      .pipe(
        switchMap(outcome =>
          outcome === 'failed'
            ? endSession(req, authStore, router)
            : sendWithToken(req, next, authStore, router)
        )
      );
  }
  return sendWithToken(req, next, authStore, router);
};

function sendWithToken(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authStore: AuthStoreInstance,
  router: Router
): Observable<HttpEvent<unknown>> {
  const token = authStore.accessToken();
  const authReq = token ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== UNAUTHORIZED) {
        return throwError(() => error);
      }

      return authStore.refresh().pipe(
        tap(outcome => {
          if (outcome === 'failed') {
            logoutIfSignedIn(authStore, router);
          }
        }),
        // Only a failing refresh maps to the original 401; the replay's own error (a 409, a 500)
        // reaches the caller as it is, so a refreshed write still reports what the server said.
        catchError(() => throwError(() => error)),
        switchMap(outcome => retryWithFreshToken(outcome, authStore, req, next, error))
      );
    })
  );
}

/** Ends a session whose token could not be renewed, without sending the call it was for. */
function endSession(
  req: HttpRequest<unknown>,
  authStore: AuthStoreInstance,
  router: Router
): Observable<never> {
  logoutIfSignedIn(authStore, router);
  return throwError(
    () => new HttpErrorResponse({ status: UNAUTHORIZED, statusText: 'Unauthorized', url: req.url })
  );
}

function logoutIfSignedIn(authStore: AuthStoreInstance, router: Router): void {
  if (authStore.isAuthenticated()) {
    authStore.logout(pageToComeBackTo(router));
  }
}

/**
 * The URL the visitor is on or on their way to. Calls made at start-up (the host, the first
 * page's data) fail before the first navigation ends, while `router.url` still reads `/`.
 */
function pageToComeBackTo(router: Router): string {
  const pending = router.currentNavigation();
  return pending ? router.serializeUrl(pending.finalUrl ?? pending.extractedUrl) : router.url;
}

/**
 * Replays the failed request with the refreshed token, or rethrows the original 401. A stale
 * refresh belonged to a session that has since ended or been replaced: the request is not
 * replayed under the new session's token.
 */
function retryWithFreshToken(
  outcome: RefreshOutcome,
  authStore: AuthStoreInstance,
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  error: HttpErrorResponse
): Observable<HttpEvent<unknown>> {
  const newToken = authStore.accessToken();
  if (outcome !== 'refreshed' || !newToken) {
    return throwError(() => error);
  }
  return next(req.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } }));
}
